/**
 * CustomerInfoModal — everything the runsheet holds about the selected stop's customer,
 * read-only. Opened from the customer detail.
 */

import { Group, Stack, Text } from '@mantine/core';
import { ModalFrame } from './ModalFrame';
import type { RunsheetLine } from '@/types';

interface InfoRow {
  label: string;
  value: string;
}

function infoRows(current: RunsheetLine): InfoRow[] {
  const mapRef = `${current.ubd_map ?? ''} ${current.ubd_c1 ?? ''} ${current.ubd_c2 ?? ''}`.trim();
  const rows: InfoRow[] = [
    { label: 'Address', value: current.deliveryaddress },
    { label: 'Map Ref', value: mapRef },
    { label: 'Customer', value: current.namewithid },
    { label: 'Pickup Code', value: current.pickupcode },
    { label: 'Bags', value: String(current.num_bags) },
    { label: 'Rate', value: `$${current.cost} / bag` },
    { label: 'Mobile', value: current.ph_mob },
    { label: 'Home', value: current.ph_home },
    { label: 'Work', value: current.ph_work },
    { label: 'Driver Notes', value: current.driver_notes },
    { label: 'Office Notes', value: current.cont_notes },
    { label: 'Notes', value: current.cust_notes },
  ];
  return rows.filter(({ value }) => value);
}

interface Props {
  opened: boolean;
  current: RunsheetLine;
  onClose: () => void;
}

export function CustomerInfoModal({ opened, current, onClose }: Props) {
  return (
    <ModalFrame opened={opened} onClose={onClose} title={current.namewithid} size="lg">
      <Stack gap={6}>
        {infoRows(current).map(({ label, value }) => (
          <Group key={label} gap="xs" align="flex-start" wrap="nowrap">
            <Text fz="sm" c="dimmed" w={96} style={{ flexShrink: 0 }}>
              {label}:
            </Text>
            {/* pre-line keeps the line breaks the office and the driver type into notes. */}
            <Text
              style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
            >
              {value}
            </Text>
          </Group>
        ))}
      </Stack>
    </ModalFrame>
  );
}
